/**
 * Bangla (Bengali) localization utilities.
 *
 * This replaces four near-duplicate functions from the original screen:
 * `engToBdNum`, `ReplaceTextFunction`, `ReplaceTextDayFunction`, and
 * `ReplaceTextFunctionNumber`.
 *
 * Why the originals were buggy:
 * They chained dozens of `someString.replace('12', '১২')` calls.
 * `String.prototype.replace(pattern, ...)` with a plain string pattern only
 * replaces the FIRST match. So on a string like "12:12 PM", only the first
 * "12" got converted. Order also mattered a lot (e.g. replacing '1' before
 * '12' would corrupt '12'), which made the functions fragile and easy to
 * break when adding a new year.
 *
 * The fix: a single global regex conversion per digit. This is shorter,
 * correct for repeated digits, and doesn't need a new line added every year.
 */

const BANGLA_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

/** Converts every ASCII digit (0-9) in `value` to its Bangla numeral. */
export function toBanglaNumerals(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[0-9]/g, digit => BANGLA_DIGITS[Number(digit)]);
}

const MERIDIEM_BN = { AM: 'এ এম', PM: 'পি এম' };

/**
 * Converts a formatted date/time string (e.g. "05-07-2026 09:30 AM") to
 * Bangla numerals and localizes AM/PM. Day names are handled separately,
 * see `toBanglaDayName`.
 */
export function toBanglaDateTimeString(value) {
    if (value === null || value === undefined) return '';
    return toBanglaNumerals(value)
        .replace('AM', MERIDIEM_BN.AM)
        .replace('PM', MERIDIEM_BN.PM);
}

const BANGLA_DAY_NAMES = {
    Saturday: 'শনিবার',
    Sunday: 'রবিবার',
    Monday: 'সোমবার',
    Tuesday: 'মঙ্গলবার',
    Wednesday: 'বুধবার',
    Thursday: 'বৃহস্পতিবার',
    Friday: 'শুক্রবার',
};

/** Converts an English day name (e.g. "Sunday") to its Bangla equivalent. */
export function toBanglaDayName(englishDayName) {
    return BANGLA_DAY_NAMES[englishDayName] ?? englishDayName;
}